import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  KeyRound,
  Lock,
  RefreshCw,
  WifiOff
} from 'lucide-react'
import LoadingSpinner from '@components/ui/LoadingSpinner'

const STATE_CONTENT = {
  forbidden: {
    icon: Lock,
    title: 'Administrator access required',
    description:
      'This account does not have permission to open the Platform Console.'
  },
  mfaEnrollmentRequired: {
    icon: KeyRound,
    title: 'MFA enrollment required',
    description:
      'Set up multi-factor authentication before using administrator tools.'
  },
  sessionExpired: {
    icon: AlertTriangle,
    title: 'Session expired',
    description:
      'Your authenticated session is no longer valid. Reload to sign in again.'
  },
  unavailable: {
    icon: WifiOff,
    title: 'Platform Console unavailable',
    description:
      'The administrator session could not be verified. No privileged data has been loaded.'
  }
}

export default function AdminAccessState({ state, onRetry }) {
  if (state === 'loading') {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-gray-950"
        role="status"
      >
        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
          <LoadingSpinner size="md" label="Verifying administrator access" />
          Verifying administrator access
        </div>
      </div>
    )
  }

  const content = STATE_CONTENT[state] || STATE_CONTENT.unavailable
  const Icon = content.icon

  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-100 px-4 dark:bg-gray-950">
      <section className="w-full max-w-md border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex h-10 w-10 items-center justify-center bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <h1 className="mt-5 text-xl font-semibold text-gray-950 dark:text-white">
          {content.title}
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          {content.description}
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          {state === 'mfaEnrollmentRequired' && (
            <Link
              to="/profile"
              className="inline-flex min-h-10 items-center justify-center bg-gray-900 px-4 text-sm font-medium text-white hover:bg-gray-700 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
            >
              Open security settings
            </Link>
          )}
          {(state === 'unavailable' || state === 'sessionExpired') && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex min-h-10 items-center gap-2 bg-gray-900 px-4 text-sm font-medium text-white hover:bg-gray-700 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {state === 'sessionExpired' ? 'Reload session' : 'Retry'}
            </button>
          )}
          <Link
            to="/"
            className="inline-flex min-h-10 items-center gap-2 border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Trading dashboard
          </Link>
        </div>
      </section>
    </main>
  )
}
